import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { AddressRaw, Facility } from '@digital-platform/rs-cms/domain';
import { CmsFacilitiesService } from '@digital-platform/rs-cms/services';
import {
  FacilityLocation,
  mapConstants as map,
  mapSVGConstants as mapSvg,
  Pushpin,
} from '@digital-platform/rs-maps/domain';
import 'bingmaps';
import { environment } from '../../environments/environment';

@Component({
  selector: 'digital-platform-bing-map',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './bing-map.component.html',
  styleUrls: ['./bing-map.component.scss'],
})
export class BingMapComponent implements OnChanges, AfterViewInit {
  @ViewChild('bingMapContainer') bingMapContainer!: ElementRef;
  @Input() public pushpins: any;
  @Input() public filteredPushpins: any;
  @Input() public homepin: any;
  @Input() public stateKey: string | undefined;
  @Input() public initLat!: number;
  @Input() public initLng!: number;
  @Input() public searchLocation: AddressRaw | undefined = undefined;
  @Input() public zoom!: number;
  @Input() public isFacilityMap = false;
  @Output() updateZoom: EventEmitter<number> = new EventEmitter<number>();
  @Output() updateCoordinates: EventEmitter<any> = new EventEmitter<any>();
  @Output() updateFilter: EventEmitter<Facility[] | FacilityLocation[]> =
    new EventEmitter<Facility[] | FacilityLocation[]>();

  public isMobile = false;
  public isTablet = false;
  public screenWidth!: number;
  public map!: Microsoft.Maps.Map;
  public pushpinLocations: Microsoft.Maps.Location[] = [];
  private platformId = inject(PLATFORM_ID);

  constructor(private cmsFacilitiesService: CmsFacilitiesService) {}

  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    if (isPlatformBrowser(this.platformId)) {
      this.screenWidth = window.innerWidth;
      if (!this.isFacilityMap) {
        this.setMapConditions(this.screenWidth);
        this.updateMapView(this.initLat, this.initLng, this.zoom);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.map || !changes) return;

    const { filteredPushpins, pushpins, stateKey, searchLocation } = changes;

    // Handle non-facility map updates
    if (!this.isFacilityMap && filteredPushpins?.currentValue) {
      this.setMapConditions(this.screenWidth);
      this.setPushPins();

      // Check for initial load and orient map
      if (pushpins?.previousValue && pushpins.previousValue.length === 0) {
        this.updateMapView(this.initLat, this.initLng, this.zoom);
      }

      // Check if user cleared the search value
      if (searchLocation?.previousValue && !searchLocation?.currentValue) {
        this.setMapViewFromPushpins(filteredPushpins?.currentValue);
      }
    }

    // Handle state changes with existing pushpins
    if (stateKey?.currentValue && this.pushpinLocations?.length > 0) {
      this.setMapConditions(this.screenWidth);
      this.updateMapView(this.initLat, this.initLng, this.zoom);
    }

    // Handle search location or state updates
    if (
      filteredPushpins?.currentValue &&
      (stateKey?.currentValue || searchLocation?.currentValue)
    ) {
      this.setMapViewFromPushpins(
        filteredPushpins.currentValue,
        searchLocation?.currentValue,
        stateKey?.currentValue
      );
    }
  }

  /**
   * Updates the map view and centers it based on the pushpins provided.
   *
   * @param pushpins
   * @param searchVal
   * @param stateKey
   */
  setMapViewFromPushpins(pushpins: any[], searchVal?: any, stateKey?: string) {
    if (!pushpins.length) return;

    const locations = this.getLocations(pushpins);
    if (!locations.length) return;

    const bounds = Microsoft.Maps.LocationRect.fromLocations(locations);
    const center = bounds.center;

    const nonDesktop = this.isMobile || this.isTablet;
    const key = stateKey || this.stateKey || '';

    this.zoom = this.getZoomLevel(key, nonDesktop, searchVal);

    this.setCenteredView(
      center.latitude,
      center.longitude,
      522,
      this.map.getWidth(),
      this.zoom
    );
  }

  /**
   * Returns the zoom level based on state key and device type.
   */
  private getZoomLevel(
    key: string,
    nonDesktop: boolean,
    searchVal?: any
  ): number {
    // States that share the same zoom levels
    const largeStates = ['CA', 'FL', 'TX', 'NY'];

    if (key === 'AK') {
      return nonDesktop ? 4 : 5;
    }

    if (largeStates.includes(key)) {
      return nonDesktop ? 5 : 6;
    }

    if (searchVal || key) {
      return nonDesktop ? 8 : 9;
    }

    // Fallback zoom for anything else
    return nonDesktop ? map.mobileZoom : map.desktopZoom;
  }

  /**
   * Retrieves a list of locations based on the pushpin data
   *
   * @param pushpins
   *
   * @returns Microsoft.Maps.Location[]
   */
  getLocations(pushpins: any[]): Microsoft.Maps.Location[] {
    return (
      pushpins
        .map((facility) => {
          const lat = parseFloat(facility.latitude);
          const lng = parseFloat(facility.longitude);
          // If either lat or lng is NaN, return null so we can filter it out
          if (isNaN(lat) || isNaN(lng)) {
            return null;
          }
          return new Microsoft.Maps.Location(lat, lng);
        })
        // Now drop any nulls
        .filter((loc) => loc !== null) as Microsoft.Maps.Location[]
    );
  }

  /**
   * Centers a Bing map accounting for an overlay that covers part of the left side.
   *
   * @param latitude - The desired center latitude
   * @param longitude - The desired center longitude
   * @param overlayWidth - Width of the overlay in pixels
   * @param mapWidth - Total width of the map container in pixels
   * @param zoom - Zoom level to set
   */
  setCenteredView(
    latitude: number,
    longitude: number,
    overlayWidth: number,
    mapWidth: number,
    zoom: number
  ): void {
    // Calculate how much we need to shift the center
    const visibleWidth = mapWidth - overlayWidth;
    const offsetRatio = overlayWidth / 2 / visibleWidth;

    // Convert the original location to a pixel coordinate
    const centerPoint = this.map.tryLocationToPixel(
      new Microsoft.Maps.Location(latitude, longitude)
    );

    if (centerPoint) {
      // Calculate the longitude offset based on the current zoom level and map width
      const degreesPerPixel = 360 / Math.pow(2, zoom + 8); // approximate for mercator projection
      const longitudeOffset = -(offsetRatio * visibleWidth) * degreesPerPixel;

      // Create new location with adjusted longitude
      const offsetLocation = new Microsoft.Maps.Location(
        latitude,
        longitude + longitudeOffset
      );

      // Set the view with the offset center
      this.map.setView({
        center: offsetLocation,
        zoom: zoom,
      });
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.screenWidth = window.innerWidth;

      if (!(window as any).Microsoft) {
        this.addScriptTag(
          `https://www.bing.com/api/maps/mapcontrol?callback=initMap&key=${environment.apiKey}`,
          'bing-map'
        );
      } else {
        this.initializeMap();
      }
    }
  }

  public addScriptTag(scriptSource: string, scriptTagId: string): void {
    if (isPlatformBrowser(this.platformId)) {
      (window as any).initMap = () => {
        if (!this.map) {
          this.initializeMap();
        }
      };
    }
    if (!document.getElementById(scriptTagId)) {
      const script = document.createElement('script');

      script.type = 'text/javascript';
      script.src = scriptSource;
      script.id = scriptTagId;
      document.head.appendChild(script);
    }
  }

  public async initializeMap() {
    this.setMapConditions(this.screenWidth);
    const mapOptions = {
      credentials: environment.apiKey,
      center: new Microsoft.Maps.Location(
        this.initLat || map.desktopLat,
        this.initLng || map.desktopLng
      ),
      zoom: this.zoom,
      minZoom: this.getMinZoom(),
      enableClickableLogo: false,
      showMapTypeSelector: false,
      showZoomButtons: false,
      showLocateMeButton: false,
      showScalebar: false,
      showTermsLink: false,
      disableStreetside: true,
      disableBirdseye: true,
      showCopyright: false,
    };

    this.map = new Microsoft.Maps.Map(
      this.bingMapContainer.nativeElement,
      mapOptions
    );

    Microsoft.Maps.Events.addHandler(this.map, 'viewchangeend', () => {
      // Check if the map is rendered yet
      if (this.map.getHeight() > 0) {
        this.updatePins();
        this.updateZoom.emit(this.map.getZoom());
        this.updateCoordinates.emit(this.map.getCenter());
      }
    });

    if (this.homepin || this.filteredPushpins) {
      this.setPushPins();
    }

    if (this.stateKey && this.pushpinLocations.length > 0) {
      this.updateMapViewOnState(this.stateKey);
    }
  }

  public setMapConditions(screenWidth: number): void {
    if (this.isFacilityMap) {
      if (this.homepin.latitude && this.homepin.longitude) {
        this.initLat = +this.homepin.latitude;
        this.initLng = +this.homepin.longitude;
      }
      this.zoom = map.locationZoom;
    } else {
      this.setDeviceType(screenWidth);
      if (!this.stateKey) {
        this.setViewDefault(screenWidth);
      }
    }
  }

  public setDeviceType(screenWidth: number): void {
    const isMobile = screenWidth < 768;
    const isTablet = 767 < screenWidth && screenWidth < 1200;

    this.isMobile = isMobile;
    this.isTablet = isTablet;
  }

  public setViewDefault(screenWidth: number): void {
    const desktop = !this.isMobile && !this.isTablet;

    if (desktop) {
      this.initLat = map.desktopLat;
      this.initLng = desktop ? map.desktopLng : map.nonDesktopLng;
      this.zoom = map.desktopZoom;
    } else {
      if (!this.zoom) {
        this.zoom =
          this.isMobile || screenWidth > 1024 ? map.mobileZoom : map.tabletZoom;
        this.zoom = screenWidth < 291 ? map.smallMobileZoom : this.zoom;
      }
      if (!this.initLat || !this.initLng) {
        this.initLat = map.desktopLat;
        this.initLng = desktop ? map.desktopLng : map.nonDesktopLng;
      }
    }
  }

  public onClickPushpin(pin: Pushpin): void {
    this.cmsFacilitiesService.updateSelectedFacility(pin.trackBy);
  }

  public async setPushPins() {
    if (this.homepin) {
      this.setFacilityLocationPin();
    } else {
      this.map.entities.clear();
      this.pushpinLocations = [];
      this.facilityToPushpin(this.filteredPushpins).forEach((pin) =>
        this.createPushpin(pin)
      );
    }
  }

  public setFacilityLocationPin() {
    const pin = {
      latitude: this.initLat,
      longitude: this.initLng,
      options: {
        icon: mapSvg.homePin,
      },
    };
    this.createPushpin(pin);
  }

  public async createPushpin(pin: Pushpin): Promise<Microsoft.Maps.Pushpin> {
    const location = new Microsoft.Maps.Location(
      Number(pin.latitude),
      Number(pin.longitude)
    );
    this.pushpinLocations.push(location);
    const pushpin = new Microsoft.Maps.Pushpin(location, {
      textOffset: new Microsoft.Maps.Point(0, 8),
      text: pin.options.text,
      icon: pin.options.icon,
    });

    Microsoft.Maps.Events.addHandler(pushpin, 'click', () =>
      this.onClickPushpin(pin)
    );

    this.map.entities.push(pushpin);

    return pushpin;
  }

  public facilityToPushpin(locations: any): Pushpin[] {
    const converted: Pushpin[] = [];
    let count = 0;
    locations.forEach((facility: Facility) => {
      converted.push({
        latitude: Number(facility.latitude),
        longitude: Number(facility.longitude),
        options: {
          text: String(count + 1),
          icon: count === 0 ? mapSvg.activePin : mapSvg.defaultPin,
        },
        trackBy: facility,
      });
      count += 1;
    });
    return converted;
  }

  public updateMapView(lat: number, lng: number, zoom: number): void {
    if (this.stateKey && this.pushpinLocations.length > 0) {
      this.updateMapViewOnState(this.stateKey);
    } else {
      this.map?.setView({
        center: new Microsoft.Maps.Location(lat, lng),
        zoom: zoom,
      });
    }
  }

  public updateMapViewOnState(state: string | undefined) {
    const rect = Microsoft.Maps.LocationRect.fromLocations(
      this.pushpinLocations
    );
    const nonDesktop = this.isMobile || this.isTablet;
    const singlePin = this.pushpinLocations.length === 1;
    const multiplePinsLat = (rect.getNorth() + rect.getSouth()) / 2;
    const leftMostLng = rect.getWest();

    this.initLat = singlePin
      ? this.pushpinLocations[0].latitude
      : multiplePinsLat;

    this.initLng = leftMostLng + (nonDesktop ? 2 : -1.5);
    if (singlePin) {
      this.initLng = nonDesktop
        ? this.pushpinLocations[0].longitude
        : this.pushpinLocations[0].longitude - 5;
    }

    this.zoom = nonDesktop ? 6 : 7;
    //handle zoom level for bigger states
    switch (state) {
      case 'AK':
        this.zoom = nonDesktop ? 4 : 5;
        break;
      case 'CA':
      case 'FL':
      case 'TX':
      case 'NY':
        this.zoom = nonDesktop ? 5 : 6;
        this.initLng = nonDesktop ? leftMostLng + 3 : leftMostLng;
        break;
    }

    this.map?.setView({
      center: new Microsoft.Maps.Location(this.initLat, this.initLng),
      zoom: this.zoom,
    });
  }

  public updatePins(): void {
    const mapWidth = this.map.getWidth();

    const leftCoordinates = this.map.tryPixelToLocation(
      new Microsoft.Maps.Point(-mapWidth / 2, 0)
    ) as Microsoft.Maps.Location;

    const rightCoordinates = this.map.tryPixelToLocation(
      new Microsoft.Maps.Point(mapWidth / 2, 0)
    ) as Microsoft.Maps.Location;

    if (!this.isMobile && !this.isTablet) {
      // Making sure the boundary where the pins live is the right edge of the facility list
      const desktopSizeAdjustment = this.screenWidth >= 1440 ? 1440 : 1200;
      const listWidth = 506;
      const listMarginLeft = 16;
      const containerPadding = desktopSizeAdjustment === 1440 ? 80 : 60;

      const adjustVal = (mapWidth - desktopSizeAdjustment) / 2;

      leftCoordinates.longitude = this.calculateLongitude(
        leftCoordinates.longitude,
        rightCoordinates.longitude,
        mapWidth,
        listWidth + listMarginLeft + containerPadding + adjustVal
      );
    }

    const excludedPushpins = this.filterPushpins(
      leftCoordinates.longitude,
      rightCoordinates.longitude
    );

    this.updateFilter.emit(excludedPushpins);
  }

  private calculateLongitude(
    leftLongitude: number,
    rightLongitude: number,
    mapWidthInPixels: number,
    pixelAdjustment: number
  ): number {
    // Adjusting longitudes to consider a span over the prime meridian
    if (leftLongitude > rightLongitude) {
      rightLongitude += 360;
    }

    const longitudeRange = rightLongitude - leftLongitude;
    const pixelsPerDegree = mapWidthInPixels / longitudeRange;
    const longitudeAdjustment = pixelAdjustment / pixelsPerDegree;

    let newLongitude = leftLongitude + longitudeAdjustment;

    // Normalize the result to be within the [-180, 180] range
    newLongitude = ((newLongitude + 180) % 360) - 180;

    return newLongitude;
  }

  private getMinZoom(): number {
    return this.isMobile
      ? map.minMobileZoom
      : this.isTablet
      ? map.minTabletZoom
      : map.minDesktopZoom;
  }

  private filterPushpins(
    adjustment: number,
    rightLongitude: number
  ): Facility[] | undefined {
    const adjustedTopLeftLongitude = adjustment;

    return this.pushpins?.filter((facility: Facility) => {
      const pinLat = Number(facility.latitude);
      const pinLng = Number(facility.longitude);

      const inLatRange =
        pinLat <= this.map.getBounds().getNorthwest().latitude &&
        pinLat >= this.map.getBounds().getSoutheast().latitude;

      const normalizedPinLng = this.makeLongitudeNonNegative(pinLng);
      const normalizedTopLeftLng = this.makeLongitudeNonNegative(
        adjustedTopLeftLongitude
      );
      const normalizedBottomRightLng =
        this.makeLongitudeNonNegative(rightLongitude);

      let inLngRange;
      if (normalizedBottomRightLng < normalizedTopLeftLng) {
        inLngRange =
          normalizedPinLng >= normalizedTopLeftLng ||
          normalizedPinLng <= normalizedBottomRightLng;
      } else {
        inLngRange =
          normalizedPinLng >= normalizedTopLeftLng &&
          normalizedPinLng <= normalizedBottomRightLng;
      }

      return !inLatRange || !inLngRange;
    });
  }

  private makeLongitudeNonNegative(longitude: number): number {
    return (longitude + 360) % 360;
  }
}
